<template>
  <tr class="bg-gray-100">
    <td
      class="whitespace-nowrap border border-t-0 border-l-4 border-slate-300 border-l-primary-400 p-3 dark:border-white dark:bg-slate-700 dark:text-gray-300"
      :colspan="columnList.length"
    >
      <div v-if="isItemsMode" class="flex flex-col gap-0">
        <div v-if="itemsLoading" class="flex flex-col gap-2 p-2">
          <div
            v-for="n in 3"
            :key="n"
            class="h-8 w-full animate-pulse rounded bg-gray-200"
          />
        </div>
        <div v-else-if="!items.length" class="p-2 text-sm text-gray-500">
          {{ $t('global.no_results') }}
        </div>
        <template v-else>
          <div
            v-for="item in items"
            :key="item.id"
            class="border border-gray-300 bg-white"
          >
            <div
              class="flex cursor-pointer flex-row items-center gap-2 px-3 py-3 font-medium text-gray-700"
              @click="toggleItem(item.id)"
            >
              <FontAwesomeIcon
                :icon="['fal', isItemOpen(item.id) ? 'chevron-down' : 'chevron-right']"
                size="sm"
                class="shrink-0"
              />
              {{ $trans(item.name) }}
            </div>
            <div v-if="isItemOpen(item.id)" class="border-t border-gray-300">
              <div v-if="itemTasksLoading[item.id]" class="flex flex-col gap-2 p-2">
                <div
                  v-for="n in 2"
                  :key="n"
                  class="h-8 w-full animate-pulse rounded bg-gray-200"
                />
              </div>
              <TaskRowsTable
                v-else
                :tasks="itemTasks[item.id] ?? []"
                @refresh-list="fetchItemTasks(item.id)"
              />
            </div>
          </div>
        </template>
      </div>
      <TaskRowsTable v-else :tasks="dossier.tasks" />
    </td>
  </tr>
</template>
<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import TaskRowsTable from '@c/Dashboard/Content/Dossier/components/Body/TasksDossierTable/TaskRowsTable.vue'
import { normalizeFieldIds } from '@c/Dashboard/Content/Dossier/components/Body/components/progressBarData'

export default {
  name: 'DossierTasksTableComponent',
  components: { FontAwesomeIcon, TaskRowsTable },
  props: {
    dossier: {
      type: Object,
      required: true
    },
    columnList: {
      type: Object,
      required: true
    },
    activeField: {
      type: [Array, String],
      required: false,
      default: null
    },
    columnId: {
      type: [Number, String],
      required: false,
      default: null
    }
  },
  data() {
    return {
      itemsLoading: false,
      items: [],
      openItemIds: [],
      itemTasksLoading: {},
      itemTasks: {}
    }
  },
  computed: {
    isItemsMode() {
      return this.columnId != null
    },
    // A single key combining both toggle-source props, so switching source (chevron -> a
    // progress bar, or one progress bar -> another) triggers exactly one reset+refetch even
    // when columnId and activeField change together in the same update.
    toggleSourceKey() {
      return `${this.columnId}|${JSON.stringify(this.activeField)}`
    }
  },
  watch: {
    // A row stays mounted while switching toggle source without closing first — Vue patches
    // this instance's props rather than remounting it, so mounted() alone would never
    // refetch for the new source.
    toggleSourceKey() {
      this.resetItemsState()
    }
  },
  mounted() {
    if (this.isItemsMode) {
      this.fetchItems()
    }
  },
  methods: {
    resetItemsState() {
      this.items = []
      this.openItemIds = []
      this.itemTasks = {}
      this.itemTasksLoading = {}
      if (this.isItemsMode) {
        this.fetchItems()
      }
    },
    async fetchItems() {
      this.itemsLoading = true
      const requestKey = this.toggleSourceKey
      try {
        const fieldIds = normalizeFieldIds(this.activeField)
        const items = await this.$store.dispatch(
          'dossier/fetchItemsDossierByCategory',
          { dossier_id: this.dossier.id, category_ids: fieldIds }
        )
        // Two fetches can be in flight when the user switches toggle source before the
        // first resolves; only the response matching the CURRENT source may assign.
        if (requestKey !== this.toggleSourceKey) {
          return
        }
        this.items = items
      } catch {
        // apiClient's response interceptor already toasts the error; swallow here
        // just to avoid an unhandled rejection.
      } finally {
        this.itemsLoading = false
      }
    },
    toggleItem(itemId) {
      if (this.isItemOpen(itemId)) {
        this.openItemIds = this.openItemIds.filter((id) => id !== itemId)
        return
      }
      this.openItemIds = [...this.openItemIds, itemId]
      if (!this.itemTasks[itemId]) {
        this.fetchItemTasks(itemId)
      }
    },
    isItemOpen(itemId) {
      return this.openItemIds.includes(itemId)
    },
    async fetchItemTasks(itemId) {
      this.itemTasksLoading = { ...this.itemTasksLoading, [itemId]: true }
      try {
        const response = await this.$store.dispatch(
          'dossier/fetchItemDossierById',
          { dossier_id: this.dossier.id, item_dossier_id: itemId }
        )
        // Two fetches for the same item can be in flight (close/reopen before the first
        // resolves); only assign if the item is still open when this response lands.
        if (!this.isItemOpen(itemId)) {
          return
        }
        this.itemTasks = {
          ...this.itemTasks,
          [itemId]: response.item_dossier.tasks
        }
      } catch {
        // apiClient's response interceptor already toasts the error; swallow here
        // just to avoid an unhandled rejection.
      } finally {
        this.itemTasksLoading = { ...this.itemTasksLoading, [itemId]: false }
      }
    }
  }
}
</script>
